import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import { JournalView } from '../components/accounting/journal-view';
import { LedgerView } from '../components/accounting/ledger-view';
import { BalanceView } from '../components/accounting/balance-view';
import { useEntries, useFiscalPeriods } from '../lib/api/hooks/use-queries';
import { periodStatusLabel, pickDefaultPeriodId } from '../lib/periods';
import { todayIso } from '../lib/dates';

function AccountingPage(): JSX.Element {
  const periods = useFiscalPeriods();
  const [periodId, setPeriodId] = useState<number>(0);
  const effective = periodId > 0 ? periodId : pickDefaultPeriodId(periods.data, todayIso());
  const entries = useEntries(effective);

  if (periods.isLoading) return <Skeleton className="h-48" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comptabilité</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Label htmlFor="period-filter">Période</Label>
          <select
            id="period-filter"
            value={effective}
            onChange={(e) => setPeriodId(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {(periods.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label} ({periodStatusLabel(p.status)})
              </option>
            ))}
          </select>
        </div>

        {entries.isLoading ? (
          <Skeleton className="h-48" />
        ) : (
          <Tabs defaultValue="journal">
            <TabsList>
              <TabsTrigger value="journal">Journal</TabsTrigger>
              <TabsTrigger value="ledger">Grand-livre</TabsTrigger>
              <TabsTrigger value="balance">Balance</TabsTrigger>
            </TabsList>
            <TabsContent value="journal">
              <JournalView entries={entries.data ?? []} />
            </TabsContent>
            <TabsContent value="ledger">
              <LedgerView entries={entries.data ?? []} />
            </TabsContent>
            <TabsContent value="balance">
              <BalanceView entries={entries.data ?? []} />
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

export const Route = createFileRoute('/accounting')({ component: AccountingPage });
